import { AutoplayStatus } from '../../..//store/slices/autoplay/types';
import { PopupType } from '../../../components/popup/types';
import { BetStateBitMask, betStateBitMask, updateBetStateBitMask } from '../../../logger/BetStateBitMask';
import { Logger } from '../../../logger/Logger';
import { RoundIntegrityWatcher, RoundIntegrityWatcherPhases } from '../../../logger/RoundIntegrityWatcher';
import type { CoreStoreType } from '../../../store/CoreStore';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { betActions } from '../../../store/slices/bets/slice';
import type { BetType } from '../../../store/slices/bets/types';
import { gameStateActions } from '../../../store/slices/game/slice';
import { GameStatus } from '../../../store/slices/game/types';
import { popupActions } from '../../../store/slices/popup/slice';
import { areBetsEqual } from '../../../utils/bets';
import { roundNumber } from '../../../utils/number';
import { getAcceptedBets } from '../betUtilities/getAcceptedBets';
import { getComputedBets } from '../betUtilities/getComputedBets';
import type { ComputedBet } from '../betUtilities/types';
import type { GameCommunicationMessageType, IGameCommunicationProcessor } from '../types';

declare global {
  interface Window {
    betStateBitMask?: number;
  }
}

export class GameCommunicationBetsClosedProcessor implements IGameCommunicationProcessor {
  messageName = 'betsclosed';
  delay = 0;

  // TODO: maybe make a constructor with store setting?

  process(message: GameCommunicationMessageType, store: CoreStoreType): void {
    RoundIntegrityWatcher.setPhase(RoundIntegrityWatcherPhases.BetsClosed);

    this.logData(store);
    const tableId = store.getState().config.session?.tableId || '';

    store.dispatch(gameStateActions.setWinners([]));
    store.dispatch(gameStateActions.setCurrentGameStatus(GameStatus.BetsClosed));
    store.dispatch(betActions.saveLastBets({ tableId }));

    this.placeBets(store);
  }

  logData(store: CoreStoreType): void {
    const { bets, popup, settings } = store.getState();
    const bestLength = bets.betStack.length;
    if (popup.currentPopup?.popupName === PopupType.Inactivity) {
      updateBetStateBitMask(BetStateBitMask.Inactivity);
    } else if (popup.currentPopup !== null && popup.currentPopup?.popupName !== PopupType.InsufficientFunds) {
      updateBetStateBitMask(BetStateBitMask.OtherBlockingPopup);
    }
    if (settings.volume.master.value > 0) {
      if (settings.volume.channels['studio'].value > 0) {
        updateBetStateBitMask(BetStateBitMask.VideoSoundOn);
      }
      if (settings.volume.channels['game'].value > 0) {
        updateBetStateBitMask(BetStateBitMask.GameSoundOn);
      }
    }
    if (settings.stream.isEnabled) {
      updateBetStateBitMask(BetStateBitMask.VideoOn);
    }
    Logger.logEvent('click', 'roundClosed', Date.now(), 1, '' + (bestLength > 0), false, betStateBitMask);
    window.betStateBitMask = 0;
  }

  protected computeBets(store: CoreStoreType): ComputedBet[] {
    const { autoplay, bets } = store.getState();
    const bet = bets.betStack[bets.betStack.length - 1];
    let autoplayBets;

    /**
     *  We also assing the bets from the autoplay if it is paused to handle the case the user is present for bets open
     *  and the bet is placed but then tabs away and AP is paused and not running but the AP counter still needs to decrease because the bet has been submitted.
     */
    if (autoplay.isInProgress || autoplay.autoplayStatus === AutoplayStatus.Paused) {
      autoplayBets = autoplay.savedBet as BetType;
    }

    if (autoplayBets) {
      const totalBet =
        bets.overridingTotalBet || Object.values(bet?.betSpots || {}).reduce((acc, bet) => acc + bet.value, 0);

      if (totalBet) {
        store.dispatch(autoplayActions.decRoundCount());
      }

      if (!areBetsEqual(bet, autoplayBets) && bet && totalBet && autoplay.roundsLeft > 1) {
        store.dispatch(popupActions.pushPopup(PopupType.AutoplayModified));
      }
    }

    return getComputedBets(bets.betStack);
  }

  protected updateAcceptedBets(store: CoreStoreType, computedBets: ComputedBet[]): void {
    store.dispatch(betActions.setAcceptedBets(getAcceptedBets(computedBets)));
  }

  protected placeBets(store: CoreStoreType): void {
    const { gameState } = store.getState();

    const computedBets = this.computeBets(store);
    const totalBet = roundNumber(
      computedBets.reduce((sum, bet) => sum + bet.value, 0),
      gameState.currencyInfo?.digitsNumber ?? 0
    );

    this.checkInactivity(store, totalBet === 0);
    this.updateAcceptedBets(store, computedBets);
  }

  protected checkInactivity(store: CoreStoreType, inactiveRound = true): void {
    const { config, gameState } = store.getState();
    const inactivityLimit = config.tableConfig?.idleLimit;
    const inactiveRounds = gameState.inactivityCounter;
    if (inactiveRound) {
      if (inactivityLimit && inactiveRounds >= inactivityLimit) {
        store.dispatch(popupActions.pushPopup(PopupType.Inactivity));
        // Pause Game stream and game socket if inactivity limit is reached
        store.dispatch(gameStateActions.setGamePaused(true));
        store.dispatch(gameStateActions.resetCurrentGameStatus());
      } else {
        store.dispatch(gameStateActions.setInactivityCounter(inactiveRounds + 1));
      }
    } else if (inactiveRounds > 0) {
      store.dispatch(gameStateActions.setInactivityCounter(0));
    }
  }
}
